import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import profileImg from '../assets/images/Subject.webp';
import '../styles/About.css';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const textRef = useRef(null);

  // Wrap each word in span for interactive hover effect
  useEffect(() => {
    const paragraphs = sectionRef.current?.querySelectorAll('.about-content p');
    if (!paragraphs) return;

    paragraphs.forEach(p => {
      const text = p.textContent;
      const words = text.split(' ');
      p.innerHTML = words.map(word => `<span>${word}</span>`).join(' ');
    });
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 75%',
          end: 'top 30%',
          toggleActions: 'play none none none',
        }
      });

      tl.from(imageRef.current, {
        x: -80,
        scale: 0.9,
        opacity: 0,
        duration: 0.9,
        ease: 'power3.out'
      })
        .from(textRef.current, {
          x: 80,
          scale: 0.95,
          opacity: 0,
          duration: 0.9,
          ease: 'power3.out'
        }, '-=0.6')
        .from(
          sectionRef.current.querySelectorAll('.stat'),
          {
            y: 40,
            opacity: 0,
            scale: 0.9,
            stagger: 0.15,
            duration: 0.6,
            ease: 'power3.out'
          },
          '-=0.4'
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Refresh ScrollTrigger after images load
  useEffect(() => {
    const img = new Image();
    img.onload = () => ScrollTrigger.refresh();
    img.src = profileImg;
  }, []);

  return (
    <section ref={sectionRef} className="about" id="about">
      <div className="about-container">
        <div ref={imageRef} className="about-image">
          <div className="image-wrapper">
            <img src={profileImg} alt="Momen Medhat - Senior Python & ERPNext Developer" className="profile-img" />
            <div className="image-border"></div>
          </div>
        </div>

        <div ref={textRef} className="about-text">
          <h2 className="about-title">About Me</h2>
          <div className="about-content">
            <p>
              I'm a Full-Stack Developer and ERP Specialist who focuses on building reliable, scalable, and high-impact enterprise applications. While my core strength lies in backend architecture and complex ERP customizations, I pair it with modern frontend tools to deliver complete, frictionless solutions—from database design to polished user interfaces.
            </p>
            <p>
              My professional focus revolves around ERP systems, mission-critical integrations, and APIs using frameworks like Frappe/ERPNext, Python, Django, React, Docker, and MySQL. I specialize in turning complex operational bottlenecks into streamlined, automated software that delivers measurable business outcomes.
            </p>
            <p>
              I thrive in Agile teams, take pride in clean and maintainable code quality, and continuously push to optimize system speed, security, and integration resilience.
            </p>
          </div>
          <div className="about-stats">
            <div className="stat">
              <span className="stat-number">3+</span>
              <span className="stat-label">Years Experience</span>
            </div>
            <div className="stat">
              <span className="stat-number">10+</span>
              <span className="stat-label">Enterprise & Web Apps</span>
            </div>
            <div className="stat">
              <span className="stat-number">100%</span>
              <span className="stat-label">Commitment to Deadlines</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
